type FernainThemeMode = 'system' | 'light' | 'dark';

declare global {
    interface Window {
        FernainTheme?: {
            set(theme: FernainThemeMode): void;
            get(): FernainThemeMode;
            toggle(): void;
        };
    }
}

const storageKey = 'fernain.theme';
const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

function configuredTheme(): FernainThemeMode {
    const configured = document.documentElement.dataset.configuredTheme;
    return configured === 'light' || configured === 'dark' ? configured : 'system';
}

function preferredTheme(): FernainThemeMode {
    try {
        const stored = window.localStorage.getItem(storageKey);
        if (stored === 'system' || stored === 'light' || stored === 'dark') return stored;
    } catch {
        // Storage denial falls back to the server-approved preference.
    }
    return configuredTheme();
}

function resolvedTheme(): 'light' | 'dark' {
    return document.documentElement.getAttribute('data-resolved-theme') === 'dark' ? 'dark' : 'light';
}

function applyTheme(theme: FernainThemeMode): void {
    const resolved = theme === 'system'
        ? (mediaQuery.matches ? 'dark' : 'light')
        : theme;
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.setAttribute('data-resolved-theme', resolved);
    document.documentElement.setAttribute('data-bs-theme', resolved);
    document.querySelectorAll<HTMLElement>('[data-theme-choice]').forEach((button) => {
        button.setAttribute('aria-pressed', button.dataset.themeChoice === theme ? 'true' : 'false');
    });
    document.querySelectorAll<HTMLElement>('[data-theme-resolved-choice]').forEach((button) => {
        button.setAttribute(
            'aria-pressed',
            button.dataset.themeResolvedChoice === resolved ? 'true' : 'false'
        );
    });
    document.querySelectorAll<HTMLElement>('[data-theme-toggle]').forEach((button) => {
        const dark = resolved === 'dark';
        button.setAttribute('aria-pressed', dark ? 'true' : 'false');
        button.setAttribute('aria-label', dark ? 'Switch to light theme' : 'Switch to dark theme');
    });
    document.querySelectorAll<HTMLElement>('[data-theme-icon]').forEach((icon) => {
        const dark = resolved === 'dark';
        const show = (icon.dataset.themeIcon === 'sun') === dark;
        icon.style.display = show ? '' : 'none';
    });
}

function toggleTheme(): void {
    const next: FernainThemeMode = resolvedTheme() === 'dark' ? 'light' : 'dark';
    window.FernainTheme?.set(next);
}

window.FernainTheme = {
    set(theme: FernainThemeMode): void {
        try {
            window.localStorage.setItem(storageKey, theme);
        } catch {
            // The visual preference still applies to this page.
        }
        applyTheme(theme);
    },
    get: preferredTheme,
    toggle: toggleTheme,
};

document.addEventListener('click', (event) => {
    const target = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-theme-choice], [data-theme-toggle]');
    if (!target) return;
    if (target.hasAttribute('data-theme-toggle')) {
        event.preventDefault();
        toggleTheme();
        return;
    }
    const theme = target?.dataset.themeChoice;
    if (!target || (theme !== 'system' && theme !== 'light' && theme !== 'dark')) return;
    event.preventDefault();
    window.FernainTheme?.set(theme);
});

mediaQuery.addEventListener('change', () => {
    if (preferredTheme() === 'system') applyTheme('system');
});

document.addEventListener('DOMContentLoaded', () => {
    applyTheme(preferredTheme());
});

applyTheme(preferredTheme());

export {};
